import { useState } from 'react'
import { resetPassword } from '@/api/auth.api'
import { PasswordInput } from '@/components/ui/password-input'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { Box, Button, Container, Flex, HStack, Text, VStack, chakra } from '@chakra-ui/react'
import { useFormik } from 'formik'
import { LuInfo, LuLockKeyhole } from 'react-icons/lu'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  PasswordResetSchema,
  INITIAL_PASSWORD_RESET_VALUES,
  type PasswordResetData,
} from '@/common/auth.common'
import { mapAuthError } from '@/helper/apiError.helpers'

export default function PasswordResetPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const [screen, setScreen] = useState<'form' | 'expired' | 'success'>(() =>
    token ? 'form' : 'expired'
  )
  const navigate = useNavigate()
  const handleBack = () => navigate('/')

  const formik = useFormik<PasswordResetData>({
    initialValues: INITIAL_PASSWORD_RESET_VALUES,
    validationSchema: PasswordResetSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      if (!token) {
        toast.error('Ungültiger Link', 'Bitte fordern Sie einen neuen Zurücksetzungslink an.')
        return
      }

      try {
        await resetPassword(token, values.password)
        setScreen('success')
        toast.success(
          'Kennwort erfolgreich aktualisiert',
          'Sie werden in 5 Sekunden zum Login umgeleitet.',
          { duration: 5000 }
        )
        setTimeout(() => navigate('/login?reset=success'), 5000)
      } catch (err: any) {
        const uiError = mapAuthError(err)

        if (uiError.code === 'PASSWORD_REUSE_NOT_ALLOWED' && uiError.fieldErrors?.password) {
          setFieldError('password', uiError.fieldErrors.password)
        } else if (uiError.code === 'INVALID_PAYLOAD' && uiError.fieldErrors?.password) {
          setFieldError('password', uiError.fieldErrors.password)
        } else if (uiError.code === 'RESET_LINK_INVALID') {
          setScreen('expired')
        } else {
          toast.error(uiError.title || 'Fehler', uiError.message)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <Box minH="100vh" bg="bg.subtle">
      {/* Header */}
      <HStack
        as="header"
        justify="flex-start"
        align="center"
        w="full"
        h="60px"
        px={{ base: '4', md: '6' }}
        borderBottom="1px solid"
        borderColor="border.subtle"
        boxShadow="sm"
        bg="bg.subtle"
      >
        <Button
          variant="outline"
          size="lg"
          borderRadius="lg"
          colorPalette="gray"
          onClick={handleBack}
          borderColor="gray.muted"
        >
          Startseite
        </Button>
      </HStack>

      {/* Main */}
      <Container
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={24}
      >
        {screen === 'form' && (
          <>
            <Text
              textAlign="center"
              fontSize="2xl"
              fontWeight="medium"
              color="fg.muted"
              mt={4}
              mb={6}
            >
              Neues Kennwort festlegen
            </Text>

            <chakra.form
              onSubmit={formik.handleSubmit}
              mt={10}
              bg="bg.default"
              borderRadius="xl"
              p={4}
              w="full"
              maxW="768px"
              border="1px solid"
              borderColor="border.subtle"
              boxShadow="lg"
            >
              <VStack align="stretch" gap={4}>
                <Text textStyle="lgNormal">
                  Bitte legen Sie ein neues Kennwort für Ihr Konto fest.
                </Text>

                <Box
                  bg="blue.subtle"
                  border="1px solid"
                  borderColor="blue.subtle"
                  borderRadius="md"
                  px={4}
                  py={2}
                >
                  <HStack gap={2} align="center">
                    <LuInfo size={14} color="blue.fg" />
                    <Text textStyle="xsMedium" color="blue.fg">
                      Der Link ist nur für kurze Zeit gültig
                    </Text>
                  </HStack>
                </Box>

                <Flex gap={4} wrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Box flex="1">
                    <Text textStyle="smMedium">Neues Kennwort</Text>
                    <PasswordInput
                      id="password"
                      borderColor={'border.emphasized'}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      _invalid={
                        formik.touched.password && formik.errors.password
                          ? {
                              borderColor: 'red.500',
                              boxShadow: '0 0 0 1px var(--chakra-colors-red-500)',
                            }
                          : undefined
                      }
                      required
                    />
                    {formik.touched.password && formik.errors.password && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {formik.errors.password}
                      </Text>
                    )}
                  </Box>

                  <Box flex="1">
                    <Text textStyle="smMedium">Neues Kennwort wiederholen</Text>
                    <PasswordInput
                      id="confirm"
                      borderColor={'border.emphasized'}
                      value={formik.values.confirm}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      _invalid={
                        formik.touched.confirm && formik.errors.confirm
                          ? {
                              borderColor: 'fg.error',
                              boxShadow: '0 0 0 1px var(--chakra-colors-red-500)',
                            }
                          : undefined
                      }
                      required
                    />
                    {formik.touched.confirm && formik.errors.confirm && (
                      <Text fontSize="xs" color="fg.error" mt={1}>
                        {formik.errors.confirm}
                      </Text>
                    )}
                  </Box>
                </Flex>

                <Flex justify="flex-end">
                  <Button
                    type="submit"
                    variant="solid"
                    colorPalette="gray"
                    size="lg"
                    loading={formik.isSubmitting}
                  >
                    Kennwort speichern
                    <LuLockKeyhole style={{ marginLeft: '6px' }} size={18} />
                  </Button>
                </Flex>
              </VStack>
            </chakra.form>
          </>
        )}

        {screen === 'expired' && (
          <Box
            bg="bg.default"
            border="1px solid"
            borderColor="border.subtle"
            boxShadow="lg"
            rounded="xl"
            p={8}
            w="full"
            maxW="768px"
            textAlign="left"
          >
            <Text fontSize="xl" fontWeight="semibold" mb={4}>
              Link abgelaufen oder ungültig
            </Text>
            <Text fontSize="md" color="fg.muted" mb={6}>
              Der Zurücksetzen-Link ist nicht mehr gültig. Fordern Sie bitte einen neuen Link an.
            </Text>
            <Button colorPalette="gray" onClick={() => navigate('/forgot-password')}>
              Neuen Link anfordern
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  )
}
